/**
 * Gerenciador de Prontidão do Launcher
 * 
 * Solução arquitetural para comunicação assíncrona resiliente com o Launcher.
 * Quando o launcher está em estado "booting", as ações são enfileiradas e
 * executadas automaticamente quando o sistema ficar pronto.
 * 
 * @module LauncherReadinessManager
 */

import { sendToTerminal } from '../../terminalLogger';
import { LogCategory } from '@/config/logging';

/**
 * Estado operacional do launcher
 */
type LauncherState = 'unknown' | 'booting' | 'operational' | 'error';

/**
 * Item enfileirado para execução quando launcher estiver pronto
 */
interface QueuedAction {
  id: string;
  action: string;
  data: Record<string, any>;
  callback?: (response: any) => void;
  customTimeoutMs?: number;
  enqueuedAt: number;
  retryCount: number;
}

/**
 * Gerenciador centralizado de prontidão do launcher
 * 
 * Padrão: Singleton com Observer para notificar quando launcher ficar pronto
 */
class LauncherReadinessManager {
  private static instance: LauncherReadinessManager;
  
  private currentState: LauncherState = 'unknown';
  private stateChangedAt: number = Date.now();
  private actionQueue: QueuedAction[] = [];
  private listeners: Set<(state: LauncherState) => void> = new Set();
  private isProcessingQueue = false;
  
  // Configurações
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 2000;
  private readonly QUEUE_MAX_AGE_MS = 30000; // 30 segundos

  private constructor() {
    // Iniciar monitoramento de estado
    this.startStateMonitoring();
  }

  public static getInstance(): LauncherReadinessManager {
    if (!LauncherReadinessManager.instance) {
      LauncherReadinessManager.instance = new LauncherReadinessManager();
    }
    return LauncherReadinessManager.instance;
  }

  /**
   * Obtém o estado atual do launcher
   */
  public getState(): LauncherState {
    return this.currentState;
  }

  /**
   * Verifica se o launcher está pronto para receber comandos
   */
  public isReady(): boolean {
    return this.currentState === 'operational';
  }

  /**
   * Atualiza o estado do launcher baseado em respostas do backend
   */
  public updateStateFromResponse(response: any): void {
    const oldState = this.currentState;
    let newState: LauncherState | null = null;

    // Detectar estado booting
    if (this.isLauncherNotReadyResponse(response)) {
      newState = 'booting';
    }
    // Detectar estado operational (resposta de sucesso indica que está pronto)
    else if (this.isOperationalResponse(response)) {
      newState = 'operational';
    }

    if (newState && newState !== oldState) {
      this.setState(newState);
    }
  }

  /**
   * Verifica se a resposta indica que launcher não está pronto
   */
  private isLauncherNotReadyResponse(response: any): boolean {
    if (!response) return false;
    
    const message = response.message || response.error || '';
    const code = response.code || '';
    const result = response.result || {};
    
    return (
      message.includes('launcher_session_required') ||
      code === 'launcher_session_required' ||
      result.launcherState === 'booting'
    );
  }

  /**
   * Verifica se a resposta indica que launcher está operacional
   */
  private isOperationalResponse(response: any): boolean {
    if (!response) return false;
    
    const status = response.status;
    const result = response.result || {};
    
    // Sucesso em ações que requerem launcher pronto
    return (
      status === 'ok' &&
      !this.isLauncherNotReadyResponse(response)
    );
  }

  /**
   * Define o estado atual e notifica listeners
   */
  private setState(newState: LauncherState): void {
    const oldState = this.currentState;
    this.currentState = newState;
    this.stateChangedAt = Date.now();

    sendToTerminal(`🚀 [LAUNCHER_READINESS] Estado alterado: ${oldState} → ${newState}`, {
      oldState,
      newState,
      queueSize: this.actionQueue.length
    }, LogCategory.CONNECTION);

    // Notificar listeners
    this.listeners.forEach(listener => {
      try {
        listener(newState);
      } catch (error) {
        // Ignorar erros de listeners
      }
    });

    // Se ficou operational, processar fila (com tratamento de erro)
    if (newState === 'operational' && oldState !== 'operational') {
      this.processQueue().catch(error => {
        sendToTerminal('❌ [LAUNCHER_READINESS] Erro ao processar fila', {
          error: error instanceof Error ? error.message : String(error)
        }, LogCategory.ERROR);
      });
    }
  }

  /**
   * Enfileira uma ação para execução quando launcher estiver pronto
   */
  public enqueueAction(
    action: string,
    data: Record<string, any>,
    callback?: (response: any) => void,
    customTimeoutMs?: number
  ): string {
    const id = `queued-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const item: QueuedAction = {
      id,
      action,
      data,
      callback,
      customTimeoutMs,
      enqueuedAt: Date.now(),
      retryCount: 0
    };

    this.actionQueue.push(item);

    // Só loga se a fila estiver crescendo (evita spam)
    if (this.actionQueue.length === 1) {
      sendToTerminal('📥 [LAUNCHER_READINESS] Primeira ação enfileirada', {
        id,
        action,
        launcherState: this.currentState
      }, LogCategory.QUEUE);
    }

    return id;
  }

  /**
   * Processa a fila de ações quando launcher ficar pronto
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue) return;
    if (this.currentState !== 'operational') return;
    if (this.actionQueue.length === 0) return;

    this.isProcessingQueue = true;

    try {
      // Processar itens em ordem FIFO
      while (this.actionQueue.length > 0 && this.currentState === 'operational') {
        const item = this.actionQueue.shift();
        if (!item) continue;

        // Verificar se item não expirou
        if (Date.now() - item.enqueuedAt > this.QUEUE_MAX_AGE_MS) {
          sendToTerminal('⏰ [LAUNCHER_READINESS] Ação expirada', {
            id: item.id,
            action: item.action,
            age: Date.now() - item.enqueuedAt
          }, LogCategory.QUEUE);
          
          // Notificar callback com erro - usar setTimeout para não bloquear
          if (item.callback) {
            setTimeout(() => {
              item.callback!({
                status: 'erro',
                message: 'Ação expirou aguardando launcher. Tente novamente.',
                code: 'QUEUE_TIMEOUT'
              });
            }, 0);
          }
          continue;
        }

        // Reexecutar ação
        await this.reexecuteAction(item);
      }
    } finally {
      // Garantir que flag seja resetada mesmo se houver erro ou estado mudar
      this.isProcessingQueue = false;
      
      // Se ainda há itens e está operational, agendar novo processamento
      if (this.actionQueue.length > 0 && this.currentState === 'operational') {
        setTimeout(() => {
          this.processQueue().catch(error => {
            sendToTerminal('❌ [LAUNCHER_READINESS] Erro ao processar fila (reattempt)', {
              error: error instanceof Error ? error.message : String(error)
            }, LogCategory.ERROR);
          });
        }, 100);
      }
    }
  }

  /**
   * Reexecuta uma ação enfileirada
   */
  private async reexecuteAction(item: QueuedAction): Promise<void> {
    // Importar corelCommunication dinamicamente para evitar circular dependency
    const { corelCommunication } = await import('../corelworks');

    // Criar wrapper de callback para detectar se ainda falha
    const wrappedCallback = (response: any) => {
      // Se ainda retorna erro de launcher, re-enfileirar no INÍCIO (prioridade)
      if (this.isLauncherNotReadyResponse(response) && item.retryCount < this.MAX_RETRIES) {
        item.retryCount++;
        this.actionQueue.unshift(item); // ← unshift = início da fila (prioridade)
        
        // Silencioso - retry será processado automaticamente
      } else {
        // Chamar callback original
        if (item.callback) {
          item.callback(response);
        }
      }
    };

    // Reenviar ação
    corelCommunication.sendActionGuarded(
      item.action,
      item.data,
      wrappedCallback,
      item.customTimeoutMs
    );

    // Pequeno delay entre ações para não sobrecarregar
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Registra um listener para mudanças de estado
   */
  public onStateChange(listener: (state: LauncherState) => void): () => void {
    this.listeners.add(listener);
    
    // Retornar função para remover listener
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Inicia monitoramento periódico de estado
   */
  private startStateMonitoring(): void {
    // Verificar estado periodicamente via app-status
    setInterval(() => {
      // Se está booting há muito tempo, pode ter dado problema
      if (this.currentState === 'booting') {
        const bootingDuration = Date.now() - this.stateChangedAt;
        if (bootingDuration > 30000) { // 30 segundos
          // ✅ CORREÇÃO: Forçar operational para evitar ficar travado indefinidamente
          sendToTerminal('🔄 [LAUNCHER_READINESS] Booting >30s — forçando operational e tentando fila', {
            duration: bootingDuration,
            queueSize: this.actionQueue.length
          }, LogCategory.WARNING);
          this.setState('operational');
        } else if (bootingDuration > 60000) { // 1 minuto (log adicional)
          sendToTerminal('⚠️ [LAUNCHER_READINESS] Booting há muito tempo', {
            duration: bootingDuration
          }, LogCategory.WARNING);
        }
      }
    }, 10000); // A cada 10 segundos
  }

  /**
   * Força retry manual de todas as ações na fila
   */
  public forceRetry(): void {
    if (this.actionQueue.length === 0) return;
    
    sendToTerminal('🔁 [LAUNCHER_READINESS] Retry manual solicitado', {
      queueSize: this.actionQueue.length
    }, LogCategory.QUEUE);

    // Assumir que pode estar pronto e tentar
    this.setState('operational');
  }

  /**
   * Limpa a fila de ações
   */
  public clearQueue(reason: string = 'manual'): void {
    const count = this.actionQueue.length;
    
    // Notificar callbacks com erro
    this.actionQueue.forEach(item => {
      if (item.callback) {
        item.callback({
          status: 'erro',
          message: `Fila limpa: ${reason}`,
          code: 'QUEUE_CLEARED'
        });
      }
    });

    this.actionQueue = [];

    sendToTerminal('🗑️ [LAUNCHER_READINESS] Fila limpa', {
      reason,
      clearedCount: count
    }, LogCategory.QUEUE);
  }

  /**
   * Obtém estatísticas da fila
   */
  public getStats(): {
    state: LauncherState;
    queueSize: number;
    stateChangedAt: number;
    oldestItemAge: number | null;
  } {
    const oldestItem = this.actionQueue[0];
    const oldestItemAge = oldestItem ? Date.now() - oldestItem.enqueuedAt : null;

    return {
      state: this.currentState,
      queueSize: this.actionQueue.length,
      stateChangedAt: this.stateChangedAt,
      oldestItemAge
    };
  }
}

// Exportar singleton
export const launcherReadiness = LauncherReadinessManager.getInstance();

// Re-exportar tipos
export type { LauncherState, QueuedAction };
